<template>
<div class="container-fluid">
	<div class="row">
	    <div class="col-12">
			<h1 data-test="pageTitle">Import Physicians from CSV</h1>
		</div>


		<div v-if="physicians_imported" class="col-12">
			<div class="alert alert-success">
				<strong>{{ physicians_imported }}</strong> physicians were successfully imported. Use the <router-link :to="{ name: 'physicians.tools.duplicates' }">Duplicate Finder</router-link> to manage possible duplicate entries.
			</div>
		</div>


		<div class="col-12" v-if="csv_parsed">
			<div class="alert alert-warning">
				<strong>{{ parsed_data.total_physician_count }}</strong> rows found in CSV <span v-if="parsed_data.total_physician_count > 5">(first 5 records show)</span>.
				Please adjust field mappings (if necessary) and verify the import looks right, then click import.
			</div>
			<div class="table-responsive card">
				<img class="center-me" src="/images/loading-color.svg" v-if="physicians_importing">

				<table class="table table-hover" :class="{'inactive' : physicians_importing }">
					<thead>
						<tr>
							<th class="text-gray-500">Data field:</th>
							<th v-for="field in parsed_data.csv_fields">{{ field }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in parsed_data.csv_data">
							<td class="text-gray-500">Sample record:</td>
							<td v-for="item in row">{{ item }}</td>
						</tr>

						<tr>
							<td class="text-gray-500">Map to system:</td>
							<td v-for="(field, index) in parsed_data.csv_fields">
								<select class="form-control form-control-sm" v-model="mapped_fields[index]">
									<option v-for="model_field in parsed_data.model_fields" :value="model_field">
										{{ model_field }}
									</option>
								</select>
							</td>
						</tr>

					</tbody>
				</table>
			</div>
			<div class="text-center">
				<span class="btn btn-lg btn-secondary mr-1" @click="resetImport()" :class="{ 'disabled' : physicians_importing }">Cancel Import</span>
				<span class="btn btn-lg btn-primary" @click="runImport()" v-html="physicians_importing ? 'Import in Progress' : 'Import Physicians'" />
			</div>
		</div>


		<template v-else>
			<div class="col-12">
				<alert-errors :form="form">There were some problems with your input.</alert-errors>	
			</div>

			<div class="col">
				<div class="card h-100">
					<div class="card-body text-center text-lg">
						<div class="rounded-circle bg-gray-500 avatar-md text-3xl text-white mx-auto mt-2 mb-4 font-weight-bold">1</div>
						<p>Download the <a href="https://wfl-public.s3.amazonaws.com/PhysicianUploadTemplate.csv" class="font-weight-bold" download>template file</a> and open in Excel</p>
						<p class="text-base text-gray-500 mb-0">(An existing CSV may work, if proper data elements are present)</p>
					</div>
				</div>
			</div>

			<div class="col">
				<div class="card h-100">
					<div class="card-body text-center text-lg">
						<div class="rounded-circle bg-gray-500 avatar-md text-3xl text-white mx-auto mt-2 mb-4 font-weight-bold">2</div>
						<p>Add your records and save as CSV</p>
					</div>
				</div>
			</div>
				

			<div class="col">
				<div class="card h-100">
					<img class="center-me" src="/images/loading-color.svg" v-if="csv_uploading">
					<form @submit.prevent="submitForm" @keydown="form.onKeydown($event)" @change="form.onKeydown($event)" class="m-0" :class="{'inactive' : csv_uploading }">
						<div class="card-body text-center text-lg">
						<div class="rounded-circle bg-gray-500 avatar-md text-3xl text-white mx-auto mt-2 mb-4 font-weight-bold">3</div>
						<p>Upload your file</p>
						<file-field
							ref="csv_file"
							@file_changed="changedFile($event)"
						/>
						</div>
						<button type="submit" class="btn btn-lg btn-primary d-none">Process CSV</button>
					</form>
				</div>
			</div>

			<div class="col-12 text-center">
				<span class="btn btn-lg btn-primary mt-3" @click="submitForm()">Process CSV</span>
			</div>
		</template>


	</div>
</div>
</template>

<script>
	import { Form, HasError, AlertError, AlertErrors, AlertSuccess } from 'vform';
	import { objectToFormData } from 'object-to-formdata'
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';

	export default {
		mixins: [BreadcrumbsManager],

    	components: {
            HasError,
            AlertError,
            AlertErrors,
            AlertSuccess,
            Form,
    	},

		data() {
			return {
                form: new Form({
                	csv_file: null,
                }),

                csv_uploading: false,
                csv_parsed: null,

                parsed_data: {
                	csv_fields: null,
                	csv_data: null,
                	model_fields: null,
                	upload_file_name: null,
                	total_physician_count: null,
                },

                physicians_importing: false,
                physicians_imported: false,

                mapped_fields: [],

			}
		},

		created() {         
        },

        activated() {
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Physicians', to: { name: 'physicians.list'} },
                { text: 'Import from CSV', to: { name: 'physicians.tools.import'} },
            ]);   
        },

		methods: {
			resetImport() {
				this.csv_parsed = false;
				this.physicians_importing = false;

				this.parsed_data = {
                	csv_fields: null,
                	csv_data: null,
                	model_fields: null,
                	upload_file_name: null,
                	total_physician_count: null,
				};
			},

			changedFile($event) {
                this.form.csv_file = this.$refs.csv_file.file;
            },

            runImport() {
            	this.physicians_importing = true;
            	axios.post('/api/physicians/import-csv', { 
            		upload_file_name: this.parsed_data.upload_file_name,
            		field_mappings: this.field_mappings
            	}).then(({data}) => {
            		
            		this.physicians_imported = data;
            		this.resetImport();

            		Swal.fire({
                        toast: true,
                        position: 'top-end',
                        showConfirmButton: false,
                        timer: 4000,
                        icon: 'success',
                        title: 'Imported ' + data + ' physicians to Ember',
                    });	
            	});
            },

			submitForm() {
				this.csv_uploading = true;
				this.form.submit('post', '/api/physicians/upload-csv', {
					transformRequest: [function (data, headers) {
						return objectToFormData(data)
					}],
				}).then(({data}) => {
					this.csv_uploading = false;
					this.csv_parsed = true;
					this.parsed_data.csv_fields = data.csv_fields;
					this.parsed_data.csv_data = data.csv_data;
					this.parsed_data.model_fields = data.model_fields;
					this.parsed_data.upload_file_name = data.upload_file_name;
					this.parsed_data.total_physician_count = data.total_physician_count;

					this.mapped_fields = [...data.csv_fields];
				});
			},
		},

		computed: {
			field_mappings() {
				const mappings = [];

				this.parsed_data.csv_fields.forEach( (csv_field, index) => {
					mappings.push({
						data_field: csv_field,
						data_field_index: index,
						system_field: this.mapped_fields[index],
					})
				});

				return mappings;
			}
		}

	}
</script>

<style scoped>
	td {
		min-width:130px;
	}
	.inactive {
		opacity: .5;
	}
</style>